// app/src/lib/revenue/depositRules.js — bank-descriptor → sales-channel rules.
//
// Lives in its own dependency-free module so BOTH sales.js (which nets matching
// transactions into salesEntries) and cashflow rollups (which must EXCLUDE the
// already-netted clawbacks from expenses) can import it without a cycle
// (sales.js imports ownerDraw.js, which imports settledSales.js).

// Map a bank transaction's text to a sales channel. Only RECOGNIZED revenue
// sources count — delivery platforms by name, and in-house card settlements
// (the restaurant's own processor / merchant bankcard) → a single "Card"
// channel. Anything else (transfers, refunds, owner deposits, interest…) is
// ignored.
export const DEPOSIT_CHANNEL_RULES = [
  { keywords: ['doordash'], channel: 'DoorDash' },
  { keywords: ['grubhub'], channel: 'Grubhub' },
  { keywords: ['uber'], channel: 'Uber Eats' },
  // In-house card settlements. Covers the common restaurant POS processors and
  // bank acquirers by name; every processor labels its payouts differently, so
  // the owner can add their own descriptors via setCardDepositKeywords — those
  // are merged into this Card rule at sync time (see syncFromDeposits).
  { keywords: [
    'finix', 'merch bnkcd', 'bankcard', 'merch bankcard', 'mercury card',
    'square', 'toast', 'clover', 'stripe', 'spoton', 'touchbistro', 'lightspeed',
    'shift4', 'fiserv', 'tsys', 'worldpay', 'elavon', 'heartland', 'global payments',
  ], channel: 'Card' },
  // Cash / check deposits (teller, ATM, remote/mobile) are sales revenue too.
  // Specific terms only, so generic ACH transfers aren't miscounted. Every bank
  // labels cash/check deposits differently (e.g. "DEPOSIT ID NUMBER …"), so the
  // owner can add their own descriptors via setCashDepositKeywords — those are
  // merged into this Cash rule at sync time.
  { keywords: ['cash deposit', 'check deposit', 'remote deposit', 'mobile deposit', 'atm deposit', 'branch deposit', 'teller'], channel: 'Cash' },
];

// Explicit clawback markers. An outflow to a revenue counterparty nets into
// channel revenue ONLY when its descriptor says it is one of these. Everything
// else from the same counterparties — monthly processor fees ("CLOVER FEES"),
// platform billing ("FINIX BILLING"), app charges, equipment leases — stays an
// EXPENSE. Owner's verdict (2026-06-11): revenue must equal raw deposits,
// because sales tax is owed on sales and processing fees are not deductible
// from taxable sales (even though you can't sell without them). Finix
// per-transaction fees never reach the bank feed anyway — its deposits are
// already sales − fees; the separately-billed fees exist only on its statement.
const CLAWBACK_MARKERS = [
  'chargeback', 'charge back', 'error charge', 'reversal', 'dispute',
  'withhold', 'refund', 'adjustment', 'reclaim',
];

/**
 * The built-in rules with the owner's own bank descriptors merged in. Their
 * custom Cash/Card wording lives on userProfile; both syncFromDeposits (which
 * turns these inflows into sales) and the cashflow rollup (which must recognise
 * exactly the same inflows as revenue) build their rule list from here, so the
 * two can never disagree about what counts as a sale.
 */
export function depositRulesWithOwnerKeywords({ cashKeywords, cardKeywords } = {}) {
  const extra = { Cash: cashKeywords || [], Card: cardKeywords || [] };
  return DEPOSIT_CHANNEL_RULES.map((r) => (
    extra[r.channel] && extra[r.channel].length
      ? { ...r, keywords: [...r.keywords, ...extra[r.channel]] }
      : r
  ));
}

/** Channel name a transaction's descriptor matches, or null. */
export function matchDepositChannel(tx, rules = DEPOSIT_CHANNEL_RULES) {
  const hay = `${tx?.merchantName || ''} ${tx?.description || ''}`.toLowerCase();
  const rule = rules.find((r) => r.keywords.some((k) => hay.includes(k)));
  return rule ? rule.channel : null;
}

const DELIVERY_TYPES = new Set(['doordash', 'ubereats', 'grubhub']);
// Legacy channels created before `type` existed: fall back to the display name.
const DELIVERY_NAMES = new Set(['doordash', 'uber eats', 'ubereats', 'grubhub']);

/**
 * Which delivery platform a channel is, or null if it isn't one.
 * Type first, display name as the fallback — a book created before `type`
 * existed (or a hand-made channel) carries only a name, and screens that tested
 * `type` alone silently rendered $0 for delivery while the attribution, which
 * uses classifyChannel below, was writing those days correctly. Every screen
 * that needs a per-platform split must go through here.
 * @param {{type?:string, name?:string}} channel
 * @returns {'doordash'|'ubereats'|'grubhub'|null}
 */
export function deliveryPlatformOf(channel) {
  const t = (channel?.type || '').toLowerCase().replace(/[^a-z]/g, '');
  if (DELIVERY_TYPES.has(t)) return t;
  const n = (channel?.name || '').toLowerCase().replace(/[^a-z]/g, '');
  if (DELIVERY_TYPES.has(n)) return n;
  return null;
}

/**
 * Channel → settlement kind: 'card' | 'cash' | 'delivery' | 'pos'.
 * Classified by `type`, never by display name, so renaming a channel cannot
 * silently change how its deposits are attributed. Lives here (dependency-free)
 * because both sales.js — which now attributes deposits to sale days at WRITE
 * time — and settledSales.js need it.
 * @param {{type?:string, name?:string}} channel
 */
export function classifyChannel(channel) {
  const t = (channel?.type || '').toLowerCase();
  if (t === 'card') return 'card';
  if (t === 'cash') return 'cash';
  if (deliveryPlatformOf(channel)) return 'delivery';
  const n = (channel?.name || '').toLowerCase();
  if (n === 'card') return 'card';
  if (n === 'cash') return 'cash';
  if (DELIVERY_NAMES.has(n)) return 'delivery';
  return 'pos';
}

/**
 * True if an OUTFLOW is an EXPLICIT platform clawback (chargeback, error-charge
 * collection, payout reversal, refund collection) that syncFromDeposits nets
 * into channel revenue. Cashflow rollups must skip these on the expense side,
 * or the same dollars count twice (once as reduced revenue, once as an expense).
 *
 * Matches the static rules only (owner-defined card/cash descriptors identify
 * deposits, not clawbacks). Guards: the descriptor must carry a clawback
 * marker (fees/billing default to expense), Cash-rule outflows are
 * withdrawals, and a '*' descriptor is a card-network PURCHASE (e.g. the owner
 * ordering dinner via "DD *DOORDASH") — all of those stay expenses.
 */
export function isRevenueClawback(tx) {
  // NOTE: pending is deliberately NOT rejected here any more — syncFromDeposits
  // now counts pending deposits (with posted-twin dedup), so a pending clawback
  // must net against them or the day is overstated until it clears.
  if (!tx) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only (Plaid: >0 = out)
  const hay = `${tx.merchantName || ''} ${tx.description || ''}`.toLowerCase();
  if (hay.includes('*')) return false;
  if (!CLAWBACK_MARKERS.some((m) => hay.includes(m))) return false;
  const rule = DEPOSIT_CHANNEL_RULES.find((r) => r.keywords.some((k) => hay.includes(k)));
  return !!rule && rule.channel !== 'Cash';
}

/**
 * Find the channel a rule's money belongs in — by what the channel IS, not by
 * its display name.
 *
 * The lookup used to be exact, case-insensitive string equality against the
 * rule's label ('DoorDash', 'Uber Eats', …), and a deposit whose channel could
 * not be found was silently discarded. So a channel named "Doordash Delivery",
 * "UberEats", "Uber", or anything an operator renamed it to took its whole
 * platform's revenue to zero, permanently and with no error — while Card and
 * Cash, which are auto-created with fixed names, kept working. Matching on the
 * classified kind/platform removes that whole failure class.
 * @param {string} ruleChannel  the `channel` label from DEPOSIT_CHANNEL_RULES
 * @param {Array<{channelId:string,name?:string,type?:string}>} channels
 */
export function resolveChannelForRule(ruleChannel, channels = []) {
  const wantPlatform = deliveryPlatformOf({ name: ruleChannel });
  if (wantPlatform) {
    return channels.find((c) => deliveryPlatformOf(c) === wantPlatform)
      // Last resort: a renamed delivery channel that no longer resolves by name
      // or type still beats dropping the deposit, if it is the only one of its kind.
      || null;
  }
  const wantKind = ruleChannel.toLowerCase() === 'card' ? 'card' : 'cash';
  return channels.find((c) => classifyChannel(c) === wantKind) || null;
}
